/**
 * アプリケーション固有のJavaScript
 *
 * このファイルに独自のプログラムを記述してください。
 * ベンダーライブラリとは分離されているため、自由に編集できます。
 *
 * 機能が増えてきた場合は modules/ フォルダに分割することを推奨します。
 * 例: import { initForm } from "./modules/form.js";
 */

// ========================================
// 定数
// ========================================

// 設定値
const HEADER_OFFSET = 80; // ヘッダー固定時の追加オフセット

// クラス名（状態）
const CLASS = {
  active: "is-active",
  open: "is-open",
  scrolled: "is-scrolled",
  show: "is-show",
};

// セレクター（HTML側と連動）
const SELECTOR = {
  header: "header",
  loading: ".js-loading",
  menu: ".js-menu",
  menuButton: ".js-menuButton",
  menuDropdown: ".js-menuDropdown",
  scrollAnimation: ".js-scrollAnimation",
  anchorLink: 'a[href^="#"]',
  faqQuestion: ".js-faq-question",
  faqItem: ".l-template-faq__item",
  homeInformationTab: ".js-home-information-tab",
  homeInformationList: ".js-home-information-list",
};

// ========================================
// ユーティリティ関数
// ========================================

/**
 * ヘッダーの高さを取得（オフセット込み）
 * @returns {number} ヘッダー高さ + オフセット
 */
function getHeaderHeight() {
  // const header = document.querySelector(SELECTOR.header);
  // return header ? header.offsetHeight + HEADER_OFFSET : HEADER_OFFSET;
  return HEADER_OFFSET;
}

/**
 * 指定位置へスクロール
 * @param {number} position - スクロール位置
 * @param {string} behavior - スクロール動作 ('smooth' | 'auto')
 */
function scrollToPosition(position, behavior = "smooth") {
  window.scroll({
    top: Math.max(0, position),
    behavior,
  });
}

// ========================================
// 機能別初期化関数
// ========================================

/**
 * ハンバーガーメニューの初期化
 */
function initHamburgerMenu() {
  const menu = document.querySelector(SELECTOR.menu);
  const menuButton = document.querySelector(SELECTOR.menuButton);

  if (!menu || !menuButton) return null;

  const toggle = () => {
    menu.classList.toggle(CLASS.active);
    menuButton.classList.toggle(CLASS.active);
  };

  const close = () => {
    menu.classList.remove(CLASS.active);
    menuButton.classList.remove(CLASS.active);
  };

  const isOpen = () => menu.classList.contains(CLASS.active);

  menuButton.addEventListener("click", toggle);

  // 外部から制御できるようにオブジェクトを返す
  return { toggle, close, isOpen };
}

/**
 * ドロップダウンメニューの初期化
 */
function initDropdownMenu() {
  const dropdowns = document.querySelectorAll(SELECTOR.menuDropdown);

  if (dropdowns.length === 0) return;

  dropdowns.forEach((button) => {
    button.addEventListener("click", () => {
      button.classList.toggle(CLASS.active);
    });
  });
}

/**
 * スムーススクロールの初期化
 * @param {Object|null} hamburgerMenu - ハンバーガーメニューの制御オブジェクト
 */
function initSmoothScroll(hamburgerMenu) {
  const anchors = document.querySelectorAll(SELECTOR.anchorLink);

  if (anchors.length === 0) return;

  anchors.forEach((anchor) => {
    anchor.addEventListener("click", (e) => {
      const href = anchor.getAttribute("href");

      // 空のhrefや#のみの場合はデフォルト動作
      if (!href || href === "#") {
        e.preventDefault();
        scrollToPosition(0);
        return;
      }

      // #topの場合はページ上部へ
      if (href === "#top") {
        e.preventDefault();
        if (hamburgerMenu?.isOpen()) hamburgerMenu.close();
        scrollToPosition(0);
        return;
      }

      // 対象要素を取得
      const targetId = href.replace("#", "");
      const target = document.getElementById(targetId);

      if (!target) return; // 対象が存在しない場合はデフォルト動作

      e.preventDefault();

      // メニューが開いていれば閉じる
      if (hamburgerMenu?.isOpen()) hamburgerMenu.close();

      // スクロール位置を計算
      const headerHeight = getHeaderHeight();
      const position = window.scrollY + target.getBoundingClientRect().top - headerHeight;

      scrollToPosition(position);
    });
  });
}

/**
 * ページ遷移時のアンカー位置調整
 */
function initAnchorPositionAdjust() {
  window.addEventListener("load", () => {
    const url = location.href;

    if (!url.includes("#")) return;

    const hash = url.split("#").pop();
    if (!hash) return;

    const target = document.getElementById(hash);
    if (!target) return;

    const headerHeight = getHeaderHeight();
    const position = Math.floor(target.getBoundingClientRect().top + window.scrollY - headerHeight);

    scrollToPosition(position, "auto");
  });
}

/**
 * スクロールアニメーションの初期化（Intersection Observer）
 */
function initScrollAnimation() {
  const items = document.querySelectorAll(SELECTOR.scrollAnimation);

  if (items.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(CLASS.scrolled);
          // 一度アニメーションしたら監視を解除（パフォーマンス向上）
          observer.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: "60% 0px -40% 0px",
      threshold: 0,
    },
  );

  items.forEach((item) => observer.observe(item));
}

/**
 * FAQアコーディオンの初期化
 * - クリックした質問の親アイテムに is-open をトグル
 * - 他のアイテムは閉じる（1つのみ展開）
 * - キーボード操作（Enter / Space）にも対応
 */
function initFaqAccordion() {
  const questions = document.querySelectorAll(SELECTOR.faqQuestion);

  if (questions.length === 0) return;

  const closeAll = () => {
    questions.forEach((q) => q.classList.remove(CLASS.open));
  };

  const handleToggle = (question) => {
    const isAlreadyOpen = question.classList.contains(CLASS.open);

    closeAll();

    if (!isAlreadyOpen) {
      question.classList.add(CLASS.open);
    }
  };

  questions.forEach((question) => {
    // アクセシビリティ属性
    question.setAttribute("role", "button");
    question.setAttribute("tabindex", "0");

    question.addEventListener("click", () => handleToggle(question));

    question.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle(question);
      }
    });
  });
}

/**
 * ホームInformationタブ切替の初期化
 * - data-tab-target（タブ）と data-tab-content（リスト）を対応させて is-active を切替
 */
function initHomeInformationTabs() {
  const tabs = document.querySelectorAll(SELECTOR.homeInformationTab);
  const lists = document.querySelectorAll(SELECTOR.homeInformationList);

  if (tabs.length === 0 || lists.length === 0) return;

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const target = tab.dataset.tabTarget;

      tabs.forEach((t) => t.classList.toggle(CLASS.active, t === tab));
      lists.forEach((list) =>
        list.classList.toggle(CLASS.active, list.dataset.tabContent === target),
      );
    });
  });
}

// ========================================
// メイン初期化関数
// ========================================

/**
 * アプリケーション初期化
 */
export function initApp() {
  // ハンバーガーメニュー
  const hamburgerMenu = initHamburgerMenu();

  // ドロップダウンメニュー
  initDropdownMenu();

  // スムーススクロール（ハンバーガーメニューと連携）
  initSmoothScroll(hamburgerMenu);

  // ページ遷移時のアンカー位置調整
  initAnchorPositionAdjust();

  // スクロールアニメーション
  initScrollAnimation();

  // FAQアコーディオン
  initFaqAccordion();

  // ホーム Informationタブ切替
  initHomeInformationTabs();

  // ページ読み込み完了後にis-showクラスを追加
  const loading = document.querySelector(SELECTOR.loading);
  if (loading) {
    window.addEventListener("load", () => {
      loading.classList.add(CLASS.show);
    });
  }

  // KV ロードアニメーション
  const kvLoad = document.querySelector(".js-home-kv-load");
  if (kvLoad) {
    window.addEventListener("load", () => {
      setTimeout(() => {
        kvLoad.classList.add("is-loaded");
      }, 1200);
    });
  }

  // ========================================
  // 追加の初期化処理はここに記述
  // ========================================

  // 例: 外部モジュールの読み込み
  // import("./modules/form.js").then(({ initForm }) => {
  //   initForm();
  // });
}
